import { HttpResponse } from '@angular/common/http';
import { trimStart } from 'lodash';

export function parseFilenameFromContentDisposition(
  cd: string | null
): string | undefined {
  if (!cd) {
    return undefined;
  }
  const star = /filename\*=(?:UTF-8'')?([^;\n]+)/i.exec(cd);
  if (star?.[1]) {
    const raw = star[1].replace(/^"(.*)"$/, '$1').trim();
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const fn = /filename=(?:"([^"]+)"|([^;\s]+))/i.exec(cd);
  const name = fn?.[1] ?? fn?.[2];
  return name?.trim();
}

export function filenameFromBlobHttpResponse(
  response: HttpResponse<Blob>,
  pdfFallback?: string,
  zipFallback?: string
): string | undefined {
  const ts = Date.now();
  const pdfName = pdfFallback ?? `download-${ts}.pdf`;
  const zipName = zipFallback ?? `download-${ts}.zip`;
  const fromHeader = parseFilenameFromContentDisposition(
    response.headers.get('Content-Disposition') ??
      response.headers.get('content-disposition')
  );
  if (fromHeader) {
    return fromHeader;
  }
  const ct = (response.headers.get('Content-Type') || '').toLowerCase();
  if (ct.includes('pdf')) {
    return pdfName;
  }
  if (ct.includes('csv')) {
    return `download-${ts}.csv`;
  }
  if (ct.includes('zip')) {
    return zipName;
  }
  return undefined;
}

export const doSelectOpen = (form: any, value: string): void => {
  setTimeout(() => {
    const container = document.querySelector('.' + value + '-select-container');
    const searchInput = (container?.querySelector('.ngx-select__search') as HTMLInputElement);
    searchInput.value = form.get(value)?.value;
  });
};

export const onSearch = (event: any, field: any, form: any, value: any): void => {
  const fieldLength = field.length;

  // if the last element is the dynamic one (not available in original list), then remove it and add updated input value again
  if (fieldLength && field[0].id === 0) {
    field.shift();
  }

  form.get(value)?.setValue(event || null);

  if (!event) {
    return;
  }

  form.get(value)?.markAsDirty();
  field.unshift({ id: 0, description: event });
};

export const validateNumberInput = (event: KeyboardEvent): void => {
  const charKey = event.key;

  if (['.', 'e', 'E'].includes(charKey)) {
    event.preventDefault();
  }
};

export const syncItems = (res: any, items: any[]): any[] => {

  if (!Array.isArray(res)) {
    res = [res];
  }

  // if a new single item was added, just push it to items
  if (res.length === 1 && !items.find((row: any) => row.id === res[0].id)) {
    return [...items, res[0]];
  }

  res.map((resItem: any) => {
    if (!items.find((row: any) => row.id === resItem.id)) {
      items = [...items, resItem];
    } else {
      items = items.map((item: any) => {
        const updatedItem = res.find((row: any) => row.id === item.id);
        if (updatedItem) {
          delete updatedItem.hierarchy;
          return { ...item, ...updatedItem };
        }
        return item;
      });
    }
  });

  return items;
};

export const copyToClipboard = (selectedNodes: any) => {
  let text = '';

  selectedNodes.map((item: any) =>
    text = text + '\n' + item.data.name
  );

  navigator.clipboard.writeText(trimStart(text));
};

/**
 * Checks if any form controls in an Angular reactive form are dirty
 * @param form - The Angular reactive form to check
 * @returns true if any control is dirty, false otherwise
 */
export const hasDirtyFields = (form: any): boolean => {
  return Object.keys(form.controls).some(controlName => {
    const control = form.get(controlName);
    return control && control.dirty === true;
  });
};
